import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="privacy-page">
      <SeoHead title="Privacy Policy" description="Privacy Policy for PublicBeachAccess.com. Understand how we collect, store, and protect your personal information when you use our beach directory, including cookies, analytics, and third-party services." path="/privacy" />
      {/* Header */}
      <div className="bg-[#0EA5E9] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-['Outfit']">
            Privacy Policy
          </h1>
          <p className="text-white/80 mt-2">Last Updated: April 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              PublicBeachAccess.com ("Website"), operated by Digital Properties, LLC ("we," "our," or "us"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-3">Information Automatically Collected</h3>
            <p className="text-gray-600 mb-4">When you visit our Website, we may automatically collect certain information, including:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li>IP address and approximate location</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website or source</li>
              <li>Device information</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-3">Cookies and Tracking Technologies</h3>
            <p className="text-gray-600 mb-6">
              We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. You can control cookie settings through your browser preferences.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We may use the information we collect to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li>Provide, maintain, and improve our Website</li>
              <li>Analyze usage patterns and trends</li>
              <li>Monitor and prevent fraud or abuse</li>
              <li>Respond to your comments or inquiries</li>
              <li>Send administrative information</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">3. Google Analytics</h2>
            <p className="text-gray-600 mb-6">
              We use Google Analytics to help us understand how visitors use our Website. Google Analytics collects information such as how often users visit the site, what pages they visit, and what other sites they used prior to coming to our site. We use this information to improve our Website. Google Analytics collects only the IP address assigned to you on the date you visit our site. For more information about Google Analytics and how it collects and processes data, please visit: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline">Google Privacy Policy</a>.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">4. Information Sharing</h2>
            <p className="text-gray-600 mb-4">We may share your information in the following circumstances:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">5. Third-Party Links</h2>
            <p className="text-gray-600 mb-6">
              Our Website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any website you visit.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">6. Data Security</h2>
            <p className="text-gray-600 mb-6">
              We implement reasonable security measures to protect your information. However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">7. Your Privacy Choices</h2>
            <p className="text-gray-600 mb-4">You have choices regarding your information:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li><strong>Cookies:</strong> You can disable cookies through your browser settings</li>
              <li><strong>Do Not Track:</strong> We honor Do Not Track signals where technically feasible</li>
              <li><strong>Opt-Out:</strong> You can opt out of Google Analytics by installing the Google Analytics opt-out browser add-on</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-600 mb-6">
              Our Website is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">9. California Privacy Rights</h2>
            <p className="text-gray-600 mb-6">
              California residents may have additional rights under the California Consumer Privacy Act (CCPA). If you are a California resident and would like to exercise your rights, please contact us.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the Website after any changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">11. Contact Us</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about this Privacy Policy, please contact us at{" "}
              <a href="mailto:hello.digitalproperties@gmail.com" className="text-[#0EA5E9] hover:underline">hello.digitalproperties@gmail.com</a>{" "}
              or through our <Link to="/contact" className="text-[#0EA5E9] hover:underline">Contact Page</Link>.
            </p>

            <div className="bg-[#FEF3C7] rounded-xl p-6 mt-8">
              <p className="text-gray-700 font-medium">
                By using PublicBeachAccess.com, you consent to our Privacy Policy and agree to its terms.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
